import { uniqBy } from 'lodash';
import { useMemo } from 'react';

import { NotificationKind } from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { AppNotification } from '../../NotificationContext/context/NotificationsContext.context';
import { useNotificationContext } from '../../NotificationContext/hooks/useNotificationContext';

/**
 * Whether this notification is asking the user to review validation questions.
 * Everything that keys off "is this a validation notification" goes through
 * here, so there's one place to change if the discriminator moves again.
 */
export function isValidationNotification(
  notification: AppNotification
): boolean {
  return notification.kind === NotificationKind.ValidationQuestions;
}

/**
 * The unread validation-question notifications the app bar should call out.
 * Notifications arrive newest-first. Keep only the newest notification for
 * each household because the callout offers household-level choices.
 */
export function useValidationNotifications(): AppNotification[] {
  const { unreadValidationQuestionNotifications } = useNotificationContext();

  return useMemo(
    () =>
      uniqBy(
        unreadValidationQuestionNotifications.filter(isValidationNotification),
        (notification) => notification.household?.id ?? notification.id
      ),
    [unreadValidationQuestionNotifications]
  );
}

export function getCurrentHouseholdValidationNotification(
  notifications: AppNotification[],
  currentHouseholdId: string | null
): AppNotification | undefined {
  if (!currentHouseholdId) return undefined;
  return notifications.find(
    (notification) => notification.household?.id === currentHouseholdId
  );
}

export interface ValidationNotificationTarget {
  householdId: string;
  conversationId: string;
}

/**
 * The household agent conversation a validation notification points at, or
 * null when the notification is missing the household or the conversation
 * target it needs to be openable.
 */
export function getValidationNotificationTarget(
  notification: AppNotification
): ValidationNotificationTarget | null {
  const householdId = notification.household?.id;
  const conversationId = notification.data?.targetIDs?.find(
    (id) => getPulidKind(id) === PulidKind.Conversation
  );

  if (!householdId || !conversationId) return null;

  return { householdId, conversationId };
}
